import { CommonButton } from "@components/CommonButton";
import { useAppDispatch, useAppSelector } from "@hooks/redux";
import { type LinkedAccount, selectIsUnbinding, unbindLinkedAccount } from "@store/linkedAccountSlice";
import { Modal } from "antd";
import { Trans } from "react-i18next";

interface DisconnectConfirmModalProps {
  open: boolean;
  account: LinkedAccount;
  onClose: () => void;
}

const PLATFORM_DISPLAY: Record<string, string> = {
  TIKTOK: "TikTok",
};

const BellAlertIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 28 28" fill="none" aria-hidden xmlns="http://www.w3.org/2000/svg">
    <path
      d="M14 4.667c-3.222 0-5.833 2.612-5.833 5.833v2.917l-1.75 2.916h15.166l-1.75-2.916V10.5c0-3.221-2.611-5.833-5.833-5.833Z"
      fill="currentColor"
    />
    <path d="M11.083 19.25a2.917 2.917 0 1 0 5.834 0h-5.834Z" fill="currentColor" />
    <path
      d="m22.167 5.833 1.75 1.75M5.833 5.833 4.083 7.583"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
    />
  </svg>
);

export const DisconnectConfirmModal = ({ open, account, onClose }: DisconnectConfirmModalProps) => {
  const dispatch = useAppDispatch();
  const accountId = account.id ?? -1;
  const isUnbinding = useAppSelector(selectIsUnbinding(accountId));
  const platformName = PLATFORM_DISPLAY[account.platform ?? "TIKTOK"] ?? "TikTok";

  const handleDisconnect = async () => {
    if (accountId < 0) return;
    try {
      await dispatch(unbindLinkedAccount(accountId)).unwrap();
      onClose();
    } catch (error) {
      console.error("[disconnect linked account]", error);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={() => {
        if (!isUnbinding) onClose();
      }}
      footer={null}
      width={440}
      centered
      destroyOnHidden
      mask={{ closable: !isUnbinding }}
      closable={false}
      styles={{ body: { padding: 0 }, container: { padding: 0 } }}
    >
      <div className="flex flex-col items-center bg-kds-bg-card">
        <div className="relative flex h-[64px] w-full items-center justify-center pt-[24px]">
          <div className="flex size-[56px] items-center justify-center rounded-full bg-kds-bg-overlay text-kds-fg">
            <BellAlertIcon className="size-[28px]" />
          </div>
        </div>
        <div className="flex w-full flex-col items-center gap-[4px] px-[16px] pt-[24px] pb-[16px]">
          <p className="text-[16px] font-semibold leading-[24px] text-kds-fg">
            <Trans>🥺 Wait, leaving so soon?</Trans>
          </p>
          <div className="w-full text-left text-[14px] font-normal leading-[1.4] text-kds-fg">
            <p className="mb-0">
              <Trans>Your peers are gaining massive exposure through Kaloclip! If you disconnect, you will lose:</Trans>
            </p>
            <p className="mb-0">
              {` 1. `}
              <Trans
                i18nKey="disconnectModal.lose.publishing"
                values={{ platform: platformName }}
                defaults="1-Click Publishing: You'll have to manually download, transfer, and re-upload every piece of viral content to {{platform}}."
              />
            </p>
            <p>
              {`2. `}
              <Trans>
                Automated Data Insights: You'll lose auto-analysis of your content performance, making it harder to
                create your next viral hit.
              </Trans>
            </p>
          </div>
        </div>
        <div className="flex w-full items-center justify-end gap-[8px] border-t border-kds-border p-[16px]">
          <CommonButton
            type="default"
            size="middle"
            loading={isUnbinding}
            onClick={handleDisconnect}
            className="w-[100px]!"
          >
            <Trans>Disconnect</Trans>
          </CommonButton>
          <CommonButton type="primary" size="middle" disabled={isUnbinding} onClick={onClose}>
            <Trans>Keep Connected</Trans>
          </CommonButton>
        </div>
      </div>
    </Modal>
  );
};
